import { getValidClaimIntakeBearerToken } from '@circuitry-ai/doc-data';
import {
  pickClaimIntakeYieldAttributes,
  trackClaimIntakeDifyStream,
  trackClaimIntakeStartStream,
} from './claimIntakeRum';
import { getClaimScoreAgentsStreamBaseUrl, requireAgentsStreamBaseUrl } from './claimScoreAgentApi';
import { normalizeClaimScoreSseEnvelope } from './difyWorkflowFinished';
import {
  isClaimSubmitWorkflowErrorResponse,
  isPcmiServerErrorStatus,
  PCMI_SERVER_ERROR_MESSAGE,
  readPcmiStatusCode,
} from './claimSubmitErrorUtils';

export const CLAIM_INTAKE_AGENT_NAME = 'claim intake agent';

/** Re-export so callers don't need to import from claimScoreAgentApi directly. */
export { getClaimScoreAgentsStreamBaseUrl as getClaimIntakeAgentsStreamBaseUrl };

/** Established once on page load; reused for all dify_stream calls. */
export type ClaimIntakeSession = Readonly<{
  aiworkerId: string;
  sessionId: string;
}>;

/** Sent inside `user_input.event` on every claim-intake `dify_stream` call so the workflow can branch. */
export type ClaimIntakeDifyEvent =
  | 'VIN'
  | 'estimateparser'
  | '3csmapper'
  | 'saveclaim'
  | 'previous_claims'
  | 'servicer_info'
  | 'existing_claim'
  | 'multiple_claims'
  | 'query';

function pickStr(obj: Record<string, unknown>, snake: string, camel: string): string | undefined {
  const a = obj[snake];
  if (typeof a === 'string' && a) return a;
  const b = obj[camel];
  if (typeof b === 'string' && b) return b;
  return undefined;
}

/**
 * Calls POST /start-stream for the claim intake workflow.
 * Should be called once when the page loads (as soon as aiworkerId + token are available).
 * The returned session is then passed to every `runClaimIntakeOperation` call.
 */
async function initClaimIntakeSessionUntracked(params: {
  aiworkerId: string;
  token: string;
  baseUrl?: string;
  signal?: AbortSignal;
}): Promise<ClaimIntakeSession> {
  const base = requireAgentsStreamBaseUrl(params.baseUrl);
  const bearer = await getValidClaimIntakeBearerToken(params.token);
  const response = await fetch(`${base}/start-stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      Authorization: `Bearer ${bearer}`,
    },
    body: JSON.stringify({ payload: { aiworker_id: params.aiworkerId } }),
    signal: params.signal,
  });

  if (!response.ok) {
    const err = await response.text().catch(() => '');
    throw new Error(`claim intake start-stream failed ${response.status}: ${err}`);
  }

  let raw: Record<string, unknown> = {};
  const ct = response.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    raw = (await response.json()) as Record<string, unknown>;
  } else {
    raw = { _rawText: await response.text() };
  }
  const flat =
    raw.payload != null && typeof raw.payload === 'object' && !Array.isArray(raw.payload)
      ? (raw.payload as Record<string, unknown>)
      : raw;

  const aiworkerId = pickStr(flat, 'aiworker_id', 'aiworkerId') ?? params.aiworkerId;
  const sessionId = pickStr(flat, 'session_id', 'sessionId') ?? '';
  if (!sessionId) throw new Error('claim intake start-stream: missing session_id in response');

  return { aiworkerId, sessionId };
}

export async function initClaimIntakeSession(params: {
  aiworkerId: string;
  token: string;
  baseUrl?: string;
  signal?: AbortSignal;
}): Promise<ClaimIntakeSession> {
  return trackClaimIntakeStartStream({
    aiworkerId: params.aiworkerId,
    run: () => initClaimIntakeSessionUntracked(params),
  });
}

type SseExtracted = Readonly<{
  text: string | null;
  aiworkerUsageId: string | null;
  workflowFailedMessage: string | null;
  servicerClassification: string | null;
  pcmiStatusCode: number | null;
  pendingClaimNumbers: string[];
}>;

/** A new claim is blocked when the VIN lookup returns any pending claim number. */
export function hasBlockingPendingClaims(claimNumbers: readonly string[]): boolean {
  return claimNumbers.length > 0;
}

export function pendingClaimBlockMessage(count: number): string {
  const verb = count === 1 ? 'is' : 'are';
  const noun = count === 1 ? 'claim' : 'claims';
  return `There ${verb} ${count} ${noun} in pending status. A new claim cannot be submitted for this vehicle until the existing claim is completed or cancelled.`;
}

function readPendingClaimNumbers(outputs: Record<string, unknown> | undefined): string[] {
  if (!outputs) return [];
  const raw = outputs.pending_claim_numbers;
  if (!Array.isArray(raw)) return [];
  const numbers: string[] = [];
  for (const item of raw) {
    if (typeof item === 'string' && item.trim()) {
      numbers.push(item.trim());
      continue;
    }
    if (typeof item === 'number' && Number.isFinite(item)) {
      numbers.push(String(item));
      continue;
    }
    if (item != null && typeof item === 'object' && !Array.isArray(item)) {
      const record = item as Record<string, unknown>;
      const nested = record.claimNumber ?? record.claim_number ?? record.number;
      if (typeof nested === 'string' && nested.trim()) numbers.push(nested.trim());
      else if (typeof nested === 'number' && Number.isFinite(nested)) numbers.push(String(nested));
    }
  }
  return numbers;
}

function readServicerClassification(outputs: Record<string, unknown> | undefined): string | null {
  const raw = outputs?.servicer_classification;
  if (typeof raw === 'string' && raw.trim()) return raw.trim();
  return null;
}

function readWorkflowFailureMessage(data: Record<string, unknown> | undefined): string | null {
  if (!data) return null;
  const status = typeof data.status === 'string' ? data.status.trim().toLowerCase() : '';
  if (status !== 'failed' && status !== 'error') return null;

  const err = data.error;
  if (typeof err === 'string' && err.trim()) return err.trim();
  if (err != null && typeof err === 'object' && !Array.isArray(err)) {
    const msg = (err as Record<string, unknown>).message;
    if (typeof msg === 'string' && msg.trim()) return msg.trim();
  }
  return 'Claim intake workflow failed.';
}

function assertClaimIntakeWorkflowSuccess(data: unknown): void {
  // existing_claim may return a claims list (possibly empty) when error is null.
  if (Array.isArray(data)) return;
  // query (and similar) may return a plain success message string in outputs.text.
  if (typeof data === 'string') return;
  if (isClaimSubmitWorkflowErrorResponse(data)) {
    throw data;
  }
}

/** `outputs.text` may be a JSON string or an already-parsed object from the workflow. */
function workflowOutputsTextToJsonString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (value != null && typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * `outputs.text` is usually JSON; saveclaim may return a plain claim id (e.g. `CL12471208`).
 */
function parseClaimIntakeOutputsText<T>(outputsText: string): T {
  const trimmed = outputsText.trim();
  if (!trimmed) {
    throw new Error('claim intake: empty outputs.text');
  }

  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (typeof parsed === 'string' && parsed.trim()) {
        const inner = parsed.trim();
        if (inner.startsWith('{') || inner.startsWith('[')) {
          return JSON.parse(inner) as T;
        }
        return parsed as T;
      }
      if (typeof parsed === 'number' || typeof parsed === 'boolean') {
        return parsed as T;
      }
    } catch {
      return trimmed as T;
    }
  }

  let parsed: unknown = JSON.parse(trimmed) as unknown;
  if (typeof parsed === 'string' && parsed.trim()) {
    const inner = parsed.trim();
    if (inner.startsWith('{') || inner.startsWith('[')) {
      parsed = JSON.parse(inner) as unknown;
    }
  }
  return parsed as T;
}

function extractOutputsFromSseBody(rawBody: string): SseExtracted {
  let text: string | null = null;
  let aiworkerUsageId: string | null = null;
  let workflowFailedMessage: string | null = null;
  let servicerClassification: string | null = null;
  let pcmiStatusCode: number | null = null;
  let pendingClaimNumbers: string[] = [];
  for (const line of rawBody.split(/\r?\n/u)) {
    let payload = line.trim();
    if (!payload || payload.startsWith(':') || payload === '[DONE]') continue;
    if (payload.startsWith('data:')) payload = payload.slice(5).trim();
    if (!payload.startsWith('{') && !payload.startsWith('[')) continue;
    try {
      const chunk = normalizeClaimScoreSseEnvelope(JSON.parse(payload));
      if (chunk?.event?.trim().toLowerCase() === 'workflow_finished') {
        const failedMsg = readWorkflowFailureMessage(chunk.data);
        if (failedMsg) workflowFailedMessage = failedMsg;
        const outputs = chunk.data?.outputs as Record<string, unknown> | undefined;
        // existing_claim returns `outputs.claims`; most other events use `outputs.text`.
        const candidates = [outputs?.text, outputs?.claims, chunk.data?.text, chunk.data?.claims];
        for (const candidate of candidates) {
          const asJson = workflowOutputsTextToJsonString(candidate);
          if (asJson) {
            text = asJson;
            break;
          }
        }
        const t = outputs?.text ?? outputs?.claims ?? chunk.data?.text;
        const uid = outputs?.aiworker_usage_id ?? chunk.data?.aiworker_usage_id;
        if (typeof uid === 'string' && uid.trim()) aiworkerUsageId = uid.trim();
        const classification = readServicerClassification(outputs);
        if (classification) servicerClassification = classification;
        const pcmi = readPcmiStatusCode(outputs) ?? readPcmiStatusCode(chunk.data) ?? readPcmiStatusCode(t);
        if (pcmi != null) pcmiStatusCode = pcmi;
        pendingClaimNumbers = readPendingClaimNumbers(outputs);
      }
    } catch {
      /* skip malformed lines */
    }
  }
  return { text, aiworkerUsageId, workflowFailedMessage, servicerClassification, pcmiStatusCode, pendingClaimNumbers };
}

/** Result type for every claim-intake operation — includes the shared `aiworker_usage_id` for logging. */
export type ClaimIntakeOperationResult<T> = Readonly<{
  data: T;
  aiworkerUsageId: string | null;
  servicerClassification: string | null;
  /** Claim numbers from `workflow_finished.data.outputs.pending_claim_numbers`. */
  pendingClaimNumbers: string[];
}>;

/**
 * Calls POST /dify_stream using an already-established session.
 * `event` is merged into `user_input` so the workflow can route (VIN lookup, estimate parse, 3Cs mapper, save claim).
 * Returns the parsed API response from `workflow_finished.data.outputs.text` plus the `aiworker_usage_id`.
 */
async function runClaimIntakeOperationUntracked<T = Record<string, unknown>>(params: {
  session: ClaimIntakeSession;
  event: ClaimIntakeDifyEvent;
  userInput: Record<string, unknown>;
  token: string;
  baseUrl?: string;
  signal?: AbortSignal;
  aiworkerUsageId?: string;
}): Promise<ClaimIntakeOperationResult<T>> {
  const base = requireAgentsStreamBaseUrl(params.baseUrl);
  const bearer = await getValidClaimIntakeBearerToken(params.token);
  const user_input: Record<string, unknown> = {
    ...params.userInput,
    event: params.event,
    ...(params.aiworkerUsageId ? { aiworker_usage_id: params.aiworkerUsageId } : {}),
  };

  const difyResponse = await fetch(`${base}/dify_stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      Authorization: `Bearer ${bearer}`,
    },
    body: JSON.stringify({
      payload: {
        aiworker_id: params.session.aiworkerId,
        session_id: params.session.sessionId,
        user_input,
      },
    }),
    signal: params.signal,
  });

  if (!difyResponse.ok) {
    const err = await difyResponse.text().catch(() => '');
    throw new Error(`claim intake dify_stream failed ${difyResponse.status}: ${err}`);
  }

  const rawBody = await difyResponse.text();
  let {
    text: outputsText,
    aiworkerUsageId,
    workflowFailedMessage,
    servicerClassification,
    pcmiStatusCode,
    pendingClaimNumbers,
  } = extractOutputsFromSseBody(rawBody);

  if (!outputsText) {
    const trimmed = rawBody.trim();
    if (trimmed.startsWith('{')) {
      try {
        const once = normalizeClaimScoreSseEnvelope(JSON.parse(trimmed));
        if (once?.event?.trim().toLowerCase() === 'workflow_finished') {
          const failedMsg = readWorkflowFailureMessage(once.data);
          if (failedMsg) workflowFailedMessage = failedMsg;
          const outputs = once.data?.outputs as Record<string, unknown> | undefined;
          const candidates = [outputs?.text, outputs?.claims, once.data?.text, once.data?.claims];
          for (const candidate of candidates) {
            const asJson = workflowOutputsTextToJsonString(candidate);
            if (asJson) {
              outputsText = asJson;
              break;
            }
          }
          const t = outputs?.text ?? outputs?.claims ?? once.data?.text;
          const uid = outputs?.aiworker_usage_id ?? once.data?.aiworker_usage_id;
          if (typeof uid === 'string' && uid.trim()) aiworkerUsageId = uid.trim();
          const classification = readServicerClassification(outputs);
          if (classification) servicerClassification = classification;
          const pcmi = readPcmiStatusCode(outputs) ?? readPcmiStatusCode(once.data) ?? readPcmiStatusCode(t);
          if (pcmi != null) pcmiStatusCode = pcmi;
          pendingClaimNumbers = readPendingClaimNumbers(outputs);
        }
      } catch {
        /* ignore */
      }
    }
  }

  if (!outputsText) {
    if (isPcmiServerErrorStatus(pcmiStatusCode)) {
      throw { error: PCMI_SERVER_ERROR_MESSAGE, pcmi_status_code: pcmiStatusCode };
    }
    if (workflowFailedMessage) {
      throw { error: workflowFailedMessage };
    }
    throw new Error('claim intake: no workflow_finished outputs.text in response');
  }

  try {
    const data = parseClaimIntakeOutputsText<T>(outputsText);
    const pcmi = readPcmiStatusCode(data) ?? pcmiStatusCode;
    if (isPcmiServerErrorStatus(pcmi)) {
      throw { error: PCMI_SERVER_ERROR_MESSAGE, pcmi_status_code: pcmi };
    }
    assertClaimIntakeWorkflowSuccess(data);
    if (!aiworkerUsageId && data != null && typeof data === 'object' && !Array.isArray(data)) {
      const nestedUid = (data as Record<string, unknown>).aiworker_usage_id;
      if (typeof nestedUid === 'string' && nestedUid.trim()) {
        aiworkerUsageId = nestedUid.trim();
      }
    }
    return { data, aiworkerUsageId, servicerClassification, pendingClaimNumbers };
  } catch (err: unknown) {
    if (err != null && typeof err === 'object' && !Array.isArray(err) && !(err instanceof Error)) {
      throw err;
    }
    throw new Error(`claim intake: outputs.text is not valid JSON: ${outputsText.slice(0, 200)}`);
  }
}

export async function runClaimIntakeOperation<T = Record<string, unknown>>(params: {
  session: ClaimIntakeSession;
  event: ClaimIntakeDifyEvent;
  userInput: Record<string, unknown>;
  token: string;
  baseUrl?: string;
  signal?: AbortSignal;
  /** Pass the aiworker_usage_id from a prior call to chain all operations under one log entry. */
  aiworkerUsageId?: string;
}): Promise<ClaimIntakeOperationResult<T>> {
  return trackClaimIntakeDifyStream({
    session: params.session,
    event: params.event,
    aiworkerUsageId: params.aiworkerUsageId,
    run: () => runClaimIntakeOperationUntracked(params),
    completeAttributes: (result) =>
      pickClaimIntakeYieldAttributes({
        aiworkerUsageId: result.aiworkerUsageId,
        servicerClassification: result.servicerClassification,
        hasWorkflowOutput: true,
      }),
  });
}
